import { useEffect, useState } from 'react';

import { ApolloError } from '@main/core-ui';
import { endpoints } from '@main/pc-types';
import { ConsentRecordSchema } from '@main/sombra-types';

import { decryptConsentIdentifiers } from '../helpers';
import { buildUseDhQuery } from './buildUseDhQuery';

const useFetchConsentPreferences = buildUseDhQuery(
  endpoints.dataSubjectConsentPreferences,
);

/**
 * Response type for the useConsentPreferences hook
 */
interface GetConsentPreferenceResponse {
  /** Consent preferences */
  data?: ConsentRecordSchema;
  /** Decrypts the DH response */
  decryptedIdentifiers: ConsentRecordSchema['identifiers'];
  /** Consent preferences loading state */
  loading: boolean;
  /** Consent preferences error state */
  error: ApolloError | null;
}

/**
 * Hook to fetch the consent preferences for a user
 *
 * @param skip - Condition to skip making the request
 * @param partition - The partition to use for the request
 * @param identifier - The identifier of the user
 * @returns Consent record of the user and decrypted identifiers
 */
export function useConsentPreferences(
  skip: boolean,
  partition?: string,
  identifier?: string,
): GetConsentPreferenceResponse {
  const [response, setResponse] = useState<GetConsentPreferenceResponse>({
    data: undefined,
    decryptedIdentifiers: [],
    loading: true,
    error: null,
  });

  const {
    data: consentPreferences,
    decryptDhResponse,
    error,
    loading,
  } = useFetchConsentPreferences({
    variables: {
      input: {
        partition: partition!,
        identifiers: identifier ? [identifier] : [],
      },
    },
    skip,
  });

  useEffect(() => {
    if (error) {
      setResponse((prev) => ({
        ...prev,
        loading: false,
        error,
      }));
    }
  }, [error]);

  useEffect(() => {
    if (consentPreferences?.decryptionContext && consentPreferences?.nodes) {
      const consentRecord = consentPreferences.nodes[0] ?? {
        purposes: [],
        identifiers: [],
      };
      const decryptedIdentifiers = decryptConsentIdentifiers(
        decryptDhResponse,
        consentPreferences.decryptionContext,
        consentRecord.identifiers,
      );
      setResponse({
        data: consentRecord,
        decryptedIdentifiers,
        loading,
        error: null,
      });
    } else if (loading) {
      setResponse((prev) => ({
        ...prev,
        loading,
      }));
    }
  }, [consentPreferences, loading]);

  return {
    ...response,
  };
}
